/**
 * Office list filter: county checkboxes + System + Bransje selects.
 * Populates System and Bransje from office cards on the page.
 * Interdependent: enable a Bransje option only if any (shown) office with the selected System has that Bransje,
 * and enable a System option only if any (shown) office with the selected Bransje has that System.
 * When counties are selected, System and Bransje options are further restricted to those that appear
 * in at least one office in the selected counties.
 * County checkboxes are disabled when they have no offices matching the current system/bransje.
 */

function normalizeSlug(str) {
	if (!str || typeof str !== "string") return "";
	return str
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "-")
		.replace(/,/g, "");
}

function parseListAttr(el, attr) {
	const raw = el.getAttribute(attr);
	if (!raw) return { values: [], labels: new Map() };
	const parts = raw
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
	const values = [];
	const labels = new Map();
	parts.forEach((p) => {
		const norm = normalizeSlug(p);
		if (norm && !labels.has(norm)) {
			labels.set(norm, p);
			values.push(norm);
		}
	});
	return { values, labels };
}

function initOfficeListFilter() {
	const section = document.querySelector(".js-office-list-filter");
	if (!section) return;

	const systemSelect = section.querySelector("#office-system");
	const bransjeSelect = section.querySelector("#office-bransje");
	const allesCheckbox = section.querySelector(".office-county-list__input--alle");
	const countyCheckboxes = section.querySelectorAll(
		'.office-county-list__input[name="office-county"]'
	);
	const locationRows = section.querySelectorAll(".row[data-county]");
	const hiddenInitially =
		section.hasAttribute("data-offices-hidden-initially") ||
		section.classList.contains("js-office-list-filter--hidden-until-filter");

	if (!locationRows.length) return;

	// All office items in this section (may be in different location rows)
	const officeItems = section.querySelectorAll(".office-list__item");

	// ---- Build unique systems and bransjer with display labels ----
	const systemLabels = new Map(); // normalized -> display
	const bransjeLabels = new Map();
	const systemSet = new Set();
	const bransjeSet = new Set();

	officeItems.forEach((item) => {
		const sys = parseListAttr(item, "data-system");
		sys.values.forEach((v) => {
			systemSet.add(v);
			if (!systemLabels.has(v)) systemLabels.set(v, sys.labels.get(v) || v);
		});
		const bransje = parseListAttr(item, "data-services");
		bransje.values.forEach((v) => {
			bransjeSet.add(v);
			if (!bransjeLabels.has(v)) bransjeLabels.set(v, bransje.labels.get(v) || v);
		});
	});

	const sortedSystems = Array.from(systemSet).sort((a, b) =>
		(systemLabels.get(a) || a).localeCompare(systemLabels.get(b) || b)
	);
	const sortedBransjer = Array.from(bransjeSet).sort((a, b) =>
		(bransjeLabels.get(a) || a).localeCompare(bransjeLabels.get(b) || b)
	);

	// Populate System select when present (keep first "Alle" option)
	if (systemSelect) {
		while (systemSelect.options.length > 1) systemSelect.remove(1);
		sortedSystems.forEach((val) => {
			const opt = document.createElement("option");
			opt.value = val;
			opt.textContent = systemLabels.get(val) || val;
			systemSelect.appendChild(opt);
		});
	}

	// Populate Bransje select when present
	if (bransjeSelect) {
		while (bransjeSelect.options.length > 1) bransjeSelect.remove(1);
		sortedBransjer.forEach((val) => {
			const opt = document.createElement("option");
			opt.value = val;
			opt.textContent = bransjeLabels.get(val) || val;
			bransjeSelect.appendChild(opt);
		});
	}

	// ---- Build index: county -> { systems, bransjer }; system -> { counties, bransjer }; bransje -> { counties, systems } ----
	const countiesBySystem = new Map();
	const bransjerBySystem = new Map();
	const countiesByBransje = new Map();
	const systemsByBransje = new Map();
	const systemsByCounty = new Map();
	const bransjerByCounty = new Map();

	officeItems.forEach((item) => {
		const row = item.closest("[data-county]");
		const county = row ? row.getAttribute("data-county") : "";
		if (!county) return;

		const sys = parseListAttr(item, "data-system");
		const bransje = parseListAttr(item, "data-services");

		sys.values.forEach((s) => {
			if (!countiesBySystem.has(s)) {
				countiesBySystem.set(s, new Set());
				bransjerBySystem.set(s, new Set());
			}
			countiesBySystem.get(s).add(county);
			bransje.values.forEach((b) => bransjerBySystem.get(s).add(b));
		});
		bransje.values.forEach((b) => {
			if (!countiesByBransje.has(b)) {
				countiesByBransje.set(b, new Set());
				systemsByBransje.set(b, new Set());
			}
			countiesByBransje.get(b).add(county);
			sys.values.forEach((s) => systemsByBransje.get(b).add(s));
		});
		if (!systemsByCounty.has(county)) {
			systemsByCounty.set(county, new Set());
			bransjerByCounty.set(county, new Set());
		}
		sys.values.forEach((s) => systemsByCounty.get(county).add(s));
		bransje.values.forEach((b) => bransjerByCounty.get(county).add(b));
	});

	function getCountyNameFromCheckbox(checkbox) {
		const label = checkbox.closest("li");
		if (!label) return "";
		const textEl = label.querySelector(".office-county-list__text");
		return textEl ? textEl.textContent.trim() : "";
	}

	function getSelectedCounties() {
		const names = [];
		countyCheckboxes.forEach((cb) => {
			if (cb.checked) names.push(getCountyNameFromCheckbox(cb));
		});
		return names.filter(Boolean);
	}

	function syncAlleCheckbox() {
		if (!allesCheckbox) return;
		const anyChecked = Array.from(countyCheckboxes).some((cb) => cb.checked);
		// On "hidden until filter" (e.g. front page), don't check "Alle" when no county selected
		if (hiddenInitially && !anyChecked) return;
		allesCheckbox.checked = !anyChecked;
	}

	function updateDisabledStates() {
		const systemVal = (systemSelect && systemSelect.value || "").trim();
		const bransjeVal = (bransjeSelect && bransjeSelect.value || "").trim();
		const selectedCounties = getSelectedCounties();

		// Counties: disable if they have no offices matching current system/bransje
		let allowedCounties = null;
		if (systemVal && bransjeVal) {
			const bySystem = countiesBySystem.get(systemVal) || new Set();
			const byBransje = countiesByBransje.get(bransjeVal) || new Set();
			allowedCounties = new Set(
				[...bySystem].filter((c) => byBransje.has(c))
			);
		} else if (systemVal) {
			allowedCounties = countiesBySystem.get(systemVal) || new Set();
		} else if (bransjeVal) {
			allowedCounties = countiesByBransje.get(bransjeVal) || new Set();
		}

		countyCheckboxes.forEach((cb) => {
			const name = getCountyNameFromCheckbox(cb);
			const disabled =
				allowedCounties !== null && name && !allowedCounties.has(name);
			cb.disabled = disabled;
			const label = cb.closest(".office-county-list__label");
			if (label) {
				if (disabled) label.setAttribute("aria-disabled", "true");
				else label.removeAttribute("aria-disabled");
			}
		});

		// When counties are selected: restrict System/Bransje options to those that appear in offices in those counties
		let systemsInSelectedCounties = null;
		let bransjerInSelectedCounties = null;
		if (selectedCounties.length > 0) {
			systemsInSelectedCounties = new Set();
			bransjerInSelectedCounties = new Set();
			selectedCounties.forEach((c) => {
				(systemsByCounty.get(c) || new Set()).forEach((s) =>
					systemsInSelectedCounties.add(s)
				);
				(bransjerByCounty.get(c) || new Set()).forEach((b) =>
					bransjerInSelectedCounties.add(b)
				);
			});
		}

		// Bransje options: enable if (1) any office with selected System has it, and (2) when counties selected, any office in those counties has it
		let allowedBransjer = null;
		if (selectedCounties.length > 0) {
			allowedBransjer = bransjerInSelectedCounties;
			if (systemVal) {
				const bySystem = bransjerBySystem.get(systemVal) || new Set();
				allowedBransjer = new Set(
					[...allowedBransjer].filter((b) => bySystem.has(b))
				);
			}
		} else if (systemVal) {
			allowedBransjer = bransjerBySystem.get(systemVal) || new Set();
		}

		// System options: enable if (1) any office with selected Bransje has it, and (2) when counties selected, any office in those counties has it
		let allowedSystems = null;
		if (selectedCounties.length > 0) {
			allowedSystems = systemsInSelectedCounties;
			if (bransjeVal) {
				const byBransje = systemsByBransje.get(bransjeVal) || new Set();
				allowedSystems = new Set(
					[...allowedSystems].filter((s) => byBransje.has(s))
				);
			}
		} else if (bransjeVal) {
			allowedSystems = systemsByBransje.get(bransjeVal) || new Set();
		}

		if (systemSelect) {
			Array.from(systemSelect.options).forEach((opt, index) => {
				if (index === 0) return; // keep "Alle" always enabled
				const val = (opt.value || "").trim();
				opt.disabled =
					allowedSystems !== null && val && !allowedSystems.has(val);
			});
		}
		if (bransjeSelect) {
			Array.from(bransjeSelect.options).forEach((opt, index) => {
				if (index === 0) return;
				const val = (opt.value || "").trim();
				opt.disabled =
					allowedBransjer !== null && val && !allowedBransjer.has(val);
			});
		}
	}

	function officeItemMatchesFilters(item, systemVal, bransjeVal) {
		const itemSystems = parseListAttr(item, "data-system").values;
		const itemBransjer = parseListAttr(item, "data-services").values;
		const matchSystem =
			!systemVal || itemSystems.some((s) => s === systemVal);
		const matchBransje =
			!bransjeVal || itemBransjer.some((b) => b === bransjeVal);
		return matchSystem && matchBransje;
	}

	function applyVisibility() {
		const systemVal = (systemSelect && systemSelect.value || "").trim();
		const bransjeVal = (bransjeSelect && bransjeSelect.value || "").trim();
		const selectedCounties = getSelectedCounties();

		// When "hidden until filter" (e.g. front page): hide all until user applies any filter (county/"Alle" or System/Bransje)
		const hasCountyFilter = selectedCounties.length > 0 || (allesCheckbox && allesCheckbox.checked);
		const hasSelectFilter = systemVal || bransjeVal;
		if (hiddenInitially && !hasCountyFilter && !hasSelectFilter) {
			locationRows.forEach((row) => {
				row.style.display = "none";
				row.setAttribute("aria-hidden", "true");
			});
			return;
		}

		let visibleCount = 0;
		locationRows.forEach((row) => {
			const county = row.getAttribute("data-county");

			if (
				selectedCounties.length > 0 &&
				!selectedCounties.includes(county)
			) {
				row.style.display = "none";
				row.setAttribute("aria-hidden", "true");
				return;
			}

			const items = row.querySelectorAll(".office-list__item");
			let hasVisibleOffice = false;
			items.forEach((item) => {
				const show = officeItemMatchesFilters(item, systemVal, bransjeVal);
				if (show) {
					item.classList.remove("is-hidden");
					item.setAttribute("aria-hidden", "false");
					hasVisibleOffice = true;
					visibleCount += 1;
				} else {
					item.classList.add("is-hidden");
					item.setAttribute("aria-hidden", "true");
				}
			});

			const showRow = hasVisibleOffice;
			row.style.display = showRow ? "" : "none";
			row.setAttribute("aria-hidden", showRow ? "false" : "true");
		});

		// Announce result count for screen readers (optional live region)
		const liveEl = section.querySelector(".js-office-list-filter-live");
		if (liveEl) {
			liveEl.textContent = visibleCount === 1
				? "1 kontor vist"
				: visibleCount + " kontorer vist";
		}

		// If focus is on a now-hidden element, move to first visible focusable or the section
		const focused = document.activeElement;
		if (focused && section.contains(focused)) {
			const isHidden = focused.closest("[aria-hidden=\"true\"]");
			if (isHidden) {
				const firstVisible = section.querySelector(".office-list__item:not(.is-hidden)");
				if (firstVisible) {
					const focusTarget = firstVisible.querySelector("a, button") || firstVisible;
					focusTarget.focus();
				} else {
					section.querySelector("button, [href], select, input")?.focus();
				}
			}
		}
	}

	function onFilterChange() {
		syncAlleCheckbox();
		updateDisabledStates();
		applyVisibility();
	}

	function clearAllFilters() {
		countyCheckboxes.forEach((cb) => {
			cb.checked = false;
		});
		if (systemSelect) systemSelect.value = "";
		if (bransjeSelect) bransjeSelect.value = "";
		onFilterChange();
	}

	if (allesCheckbox) {
		allesCheckbox.addEventListener("change", () => {
			if (allesCheckbox.checked) {
				countyCheckboxes.forEach((cb) => {
					cb.checked = false;
				});
				updateDisabledStates();
				applyVisibility();
			} else {
				// Prevent unchecking "Alle" directly with no county selected — keep it checked
				allesCheckbox.checked = true;
			}
		});
	}

	countyCheckboxes.forEach((cb) => {
		cb.addEventListener("change", onFilterChange);
	});
	if (systemSelect) systemSelect.addEventListener("change", onFilterChange);
	if (bransjeSelect) bransjeSelect.addEventListener("change", onFilterChange);

	const clearBtn = section.querySelector(".js-office-list-filter-clear");
	if (clearBtn) {
		clearBtn.addEventListener("click", clearAllFilters);
	}

	// Initial state
	syncAlleCheckbox();
	updateDisabledStates();
	applyVisibility();
}

let officeListFilterScheduled = false;
function scheduleOfficeListFilterInit() {
	if (officeListFilterScheduled) return;
	officeListFilterScheduled = true;
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			initOfficeListFilter();
		});
	} else {
		initOfficeListFilter();
	}
}

scheduleOfficeListFilterInit();
